/**
 * CollaboratorsIndicator Component
 * A compact floating indicator showing active collaborators in the adventure editor.
 * Uses the AdventureCollaborationContext for real-time participant updates.
 */

import { useState } from 'react'
import {
  Box,
  Typography,
  Avatar,
  AvatarGroup,
  Paper,
  Tooltip,
  CircularProgress,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Chip,
} from '@mui/material'
import PeopleIcon from '@mui/icons-material/People'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import { useAdventureCollaboration } from '@contexts/AdventureCollaborationContext'
import { useAuthStore } from '@store/authStore'

/**
 * Format relative time (e.g., "2m ago", "1h ago")
 */
function formatRelativeTime(timestamp: string): string {
  const now = new Date()
  const then = new Date(timestamp)
  const diffMs = now.getTime() - then.getTime()
  const diffSeconds = Math.floor(diffMs / 1000)
  const diffMinutes = Math.floor(diffSeconds / 60)
  const diffHours = Math.floor(diffMinutes / 60)

  if (diffSeconds < 60) {
    return 'just now'
  } else if (diffMinutes < 60) {
    return `${diffMinutes}m ago`
  } else if (diffHours < 24) {
    return `${diffHours}h ago`
  } else {
    return `${Math.floor(diffHours / 24)}d ago`
  }
}

/**
 * Get initials from a name
 */
function getInitials(name: string): string {
  const parts = name.split(' ').filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase()
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase()
}

/**
 * Generate a consistent color based on user ID
 */
function getUserColor(userId: string): string {
  const colors = [
    '#1976d2', // blue
    '#388e3c', // green
    '#f57c00', // orange
    '#7b1fa2', // purple
    '#c2185b', // pink
    '#0097a7', // cyan
    '#5d4037', // brown
    '#455a64', // blue-grey
  ]

  let hash = 0
  for (let i = 0; i < userId.length; i++) {
    hash = userId.charCodeAt(i) + ((hash << 5) - hash)
  }

  return colors[Math.abs(hash) % colors.length]
}

export function CollaboratorsIndicator() {
  const [isExpanded, setIsExpanded] = useState(false)
  const { participants, isConnected, isConnecting } = useAdventureCollaboration()
  const { user } = useAuthStore()

  // Filter out the current user and deduplicate by userId
  const otherParticipants = (user?.userId
    ? participants.filter(p => p.userId !== user.userId)
    : participants
  ).filter((participant, index, self) =>
    self.findIndex(p => p.userId === participant.userId) === index
  )

  // Don't show if no other collaborators
  if (isConnected && otherParticipants.length === 0) {
    return null
  }

  // Don't show if not connected and not connecting
  if (!isConnected && !isConnecting) {
    return null
  }

  const getConnectionStatus = () => {
    if (isConnecting) {
      return { color: 'warning.main', label: 'Connecting...' }
    }
    if (isConnected) {
      return { color: 'success.main', label: 'Connected' }
    }
    return { color: 'error.main', label: 'Disconnected' }
  }

  const status = getConnectionStatus()

  return (
    <Paper
      sx={{
        position: 'fixed',
        bottom: 16,
        right: 16,
        minWidth: isExpanded ? 280 : 'auto',
        maxWidth: 320,
        overflow: 'hidden',
        zIndex: 1200,
        transition: 'min-width 0.2s ease-in-out',
      }}
      elevation={4}
    >
      {/* Collapsed view - just avatars */}
      {!isExpanded && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: 1,
            cursor: 'pointer',
            '&:hover': { bgcolor: 'action.hover' },
          }}
          onClick={() => setIsExpanded(true)}
        >
          <Tooltip title={status.label}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {isConnecting ? (
                <CircularProgress size={12} sx={{ mr: 0.5 }} />
              ) : (
                <FiberManualRecordIcon
                  sx={{
                    fontSize: 10,
                    color: status.color,
                    mr: 0.5,
                    filter: isConnected ? 'drop-shadow(0 0 2px rgba(76,175,80,0.8))' : undefined,
                  }}
                />
              )}
            </Box>
          </Tooltip>
          <PeopleIcon fontSize="small" color="primary" />
          <AvatarGroup max={3} sx={{ '& .MuiAvatar-root': { width: 28, height: 28, fontSize: 12 } }}>
            {otherParticipants.map((participant) => (
              <Tooltip key={participant.userId} title={participant.userName}>
                <Avatar
                  sx={{
                    bgcolor: getUserColor(participant.userId),
                  }}
                >
                  {getInitials(participant.userName)}
                </Avatar>
              </Tooltip>
            ))}
          </AvatarGroup>
          <ExpandMoreIcon fontSize="small" color="action" />
        </Box>
      )}

      {/* Expanded view - full list */}
      <Collapse in={isExpanded}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 1.5,
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'primary.main',
            color: 'white',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title={status.label}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {isConnecting ? (
                  <CircularProgress size={14} sx={{ color: 'white' }} />
                ) : (
                  <FiberManualRecordIcon
                    sx={{
                      fontSize: 10,
                      color: status.color,
                      filter: isConnected ? 'drop-shadow(0 0 2px rgba(76,175,80,0.8))' : undefined,
                    }}
                  />
                )}
              </Box>
            </Tooltip>
            <PeopleIcon fontSize="small" />
            <Typography variant="subtitle2" fontWeight={600}>
              Collaborators
            </Typography>
            <Chip
              size="small"
              label={otherParticipants.length}
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                color: 'white',
                fontWeight: 'bold',
                height: 20,
                minWidth: 24,
              }}
            />
          </Box>
          <IconButton
            size="small"
            onClick={() => setIsExpanded(false)}
            sx={{ color: 'white' }}
          >
            <ExpandLessIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Participant List */}
        <Box sx={{ maxHeight: 240, overflow: 'auto' }}>
          {otherParticipants.length === 0 ? (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {isConnected ? 'No other collaborators' : 'Connecting...'}
              </Typography>
            </Box>
          ) : (
            <List dense disablePadding>
              {otherParticipants.map((participant) => (
                <ListItem
                  key={participant.userId}
                  sx={{
                    py: 1,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    '&:last-child': { borderBottom: 0 },
                  }}
                >
                  <ListItemAvatar sx={{ minWidth: 44 }}>
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        fontSize: 14,
                        bgcolor: getUserColor(participant.userId),
                      }}
                    >
                      {getInitials(participant.userName)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={participant.userName}
                    secondary={`Joined ${formatRelativeTime(participant.joinedAt)}`}
                    primaryTypographyProps={{
                      variant: 'body2',
                      fontWeight: 500,
                      noWrap: true,
                    }}
                    secondaryTypographyProps={{
                      variant: 'caption',
                    }}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </Collapse>
    </Paper>
  )
}

export default CollaboratorsIndicator
